import { useAuth } from 'react-oidc-context';
import { useMocks } from '@config/env';

/**
 * Returns a function that resolves the current access token, refreshing silently when needed.
 * In mock mode the OIDC `AuthProvider` isn't mounted, so we return a no-op resolver — the mock API
 * layer doesn't need a bearer token.
 *
 * The conditional hook call below is intentional and safe: `useMocks` is a module-level constant
 * fixed at import time, so the hook-call sequence is identical on every render of this hook.
 */
export function useAccessTokenProvider(): () => Promise<string | null> {
  if (useMocks) return noToken;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const auth = useAuth();
  return async () => {
    if (!auth.user) return null;
    if (auth.user.expired) {
      try {
        const refreshed = await auth.signinSilent();
        return refreshed?.access_token ?? null;
      } catch {
        return null;
      }
    }
    return auth.user.access_token ?? null;
  };
}

const noToken = async (): Promise<string | null> => null;
